import { useCallback, useState } from "react";

import { Button } from "@/components/ui/button/button";
import type { FormOptionType } from "@/components/ui/form/_types/type";
import { InputNumberField } from "@/components/ui/form/fields/input-field/input-number";
import { SelectField } from "@/components/ui/form/fields/select-field";

import type { RoomPriceRankItemType } from "../../../../../../type";

const priceType = ["ADULT_EACH", "ALL"] as const;
type PriceType = (typeof priceType)[number];

const batchRoomPriceInputOptions = [
	{ id: "batchRoomPriceInputOptions_0", label: "大人１人あたり", value: priceType[0] } satisfies FormOptionType,
	{ id: "batchRoomPriceInputOptions_1", label: "同一料金で", value: priceType[1] } satisfies FormOptionType,
] satisfies FormOptionType[];

type BatchRoomPriceObjectType = {
	priceType: PriceType;

	basePrice: number;
	price: number;
};

export type OnSetBatchRoomPriceObjectTYpe = <T extends keyof BatchRoomPriceObjectType>({
	key,
	value,
}: {
	key: T;
	value: BatchRoomPriceObjectType[T];
}) => void;

type Props = {
	roomID: string;
	priceRankItem: RoomPriceRankItemType;

	onSetRoomPriceRankItem: (value: RoomPriceRankItemType) => void;
};

export const RoomPriceRankBatchRoomPriceInput = ({
	roomID,
	priceRankItem, //
	onSetRoomPriceRankItem,
}: Props) => {
	const [batchRoomPriceObject, setBatchRoomPriceObject] = useState<BatchRoomPriceObjectType>({
		priceType: priceType[0],
		basePrice: 0,
		price: 0,
	});

	const isAdultEach = batchRoomPriceObject.priceType === "ADULT_EACH";

	const onSetBatchRoomPriceObject: OnSetBatchRoomPriceObjectTYpe = useCallback(
		({ key, value }) => {
			const mergedValue = { ...batchRoomPriceObject, [key]: value };

			setBatchRoomPriceObject(mergedValue);
		},
		[batchRoomPriceObject],
	);

	const onSetBatchRoomPriceInput = useCallback(() => {
		const newRooms = priceRankItem.rooms?.value?.map((item) => {
			if (item.roomID.value === roomID) {
				return {
					...item,
					prices: {
						isEdited: true,
						hasError: false,
						value: item.prices?.value?.map((price) => {
							const amount = isAdultEach
								? batchRoomPriceObject.price * price.adultNum.value + batchRoomPriceObject.basePrice
								: batchRoomPriceObject.price;

							return {
								...price,
								price: {
									amount: {
										...price.price.amount,
										value: amount,
									},
									currencyType: {
										...price.price.currencyType,
									},
								},
							};
						}),
					},
				};
			}

			return item;
		});

		const newPriceRankItem = {
			...priceRankItem,
			rooms: {
				isEdited: true,
				hasError: false,
				value: newRooms,
			},
		} satisfies RoomPriceRankItemType;

		onSetRoomPriceRankItem(newPriceRankItem);
	}, [
		batchRoomPriceObject.basePrice,
		batchRoomPriceObject.price,
		isAdultEach,
		onSetRoomPriceRankItem,
		priceRankItem,
		roomID,
	]);

	return (
		<div className="mb-6 flex flex-col gap-y-4">
			<div className="flex flex-wrap items-center gap-x-6 gap-y-3">
				<div className="flex items-end gap-x-2">
					<SelectField
						label="　"
						value={batchRoomPriceObject.priceType}
						options={batchRoomPriceInputOptions}
						handleValueChange={(value) =>
							onSetBatchRoomPriceObject({ key: "priceType", value: value as PriceType })
						}
					/>
					<div className="flex items-end gap-x-2">
						<InputNumberField
							value={batchRoomPriceObject.price}
							handleInput={(value) => onSetBatchRoomPriceObject({ key: "price", value: value! })}
						/>
						<p>円</p>
					</div>
				</div>
				{isAdultEach && (
					<div className="flex items-center gap-x-6">
						<p>＋</p>
						<div className="flex items-end gap-x-2">
							<InputNumberField
								label="基本料金"
								value={batchRoomPriceObject.basePrice}
								handleInput={(value) => onSetBatchRoomPriceObject({ key: "basePrice", value: value! })}
							/>
							<p>円</p>
						</div>
					</div>
				)}
			</div>
			<div>
				<Button onClick={onSetBatchRoomPriceInput} variant="outline" size="sm">
					まとめて入力する
				</Button>
			</div>
		</div>
	);
};

RoomPriceRankBatchRoomPriceInput.displayName = "RoomPriceRankBatchRoomPriceInput";
